"use client"

import React, { useState, useRef } from 'react';
import styles from './ReportTabs.module.css';
import DescriptionTab from './tabs/DescriptionTab';
import TableOfContentsTab from './tabs/TableOfContentsTab';
import TablesAndFiguresTab from './tabs/TablesAndFiguresTab';
import RelatedReportsTab from './tabs/RelatedReportsTab';

interface ReportTabsProps {
    summary: string;
    topics?: any[];
    tables?: any[];
    infographics?: any[];
    blocks?: any[];
    relatedReports?: any[];
    onSectionVisible?: (sectionId: string) => void;
    reportId?: string;
    contentId?: string;
    reportTitle?: string;
    slug?: string;
    activeTab: string;
    setActiveTab: (tabId: string) => void;
    stickyOffset?: number;
    graphAlt?: string;
    graphTitle?: string;
}

const tabs = [
    { id: 'description', label: 'Description' },
    { id: 'table-of-contents', label: 'Table of Contents' },
    { id: 'table-figures', label: 'Table & Figures' },
    { id: 'related-reports', label: 'Related Reports' },
];

const ReportTabs: React.FC<ReportTabsProps> = ({
    summary,
    topics,
    tables,
    infographics,
    blocks,
    relatedReports,
    onSectionVisible,
    reportId,
    contentId,
    reportTitle,
    slug,
    activeTab,
    setActiveTab,
    stickyOffset = 158, // Fallback to current hardcoded desktop
    graphAlt,
    graphTitle
}) => {

    const renderTabContent = () => {
        switch (activeTab) {
            case 'description':
                return (
                    <DescriptionTab
                        blocks={blocks}
                        onSectionVisible={onSectionVisible}
                        reportTitle={reportTitle}
                        reportId={reportId}
                        contentId={contentId}
                        slug={slug}
                        stickyOffset={stickyOffset}
                        graphAlt={graphAlt}
                        graphTitle={graphTitle}
                    />
                );
            case 'table-of-contents':
                return <TableOfContentsTab blocks={blocks} reportId={reportId} reportTitle={reportTitle} />;
            case 'table-figures':
                const tablesFiguresBlock = blocks?.find(b => b.block_type === 'tables_figures');
                const processedTables = tablesFiguresBlock?.block_data_json?.tables || tables || [];
                const processedFigures = tablesFiguresBlock?.block_data_json?.figures || infographics || [];
                return <TablesAndFiguresTab tables={processedTables} infographics={processedFigures} />;
            case 'related-reports':
                return <RelatedReportsTab reports={relatedReports || []} />;
            default:
                return null;
        }
    };

    const containerRef = useRef<HTMLDivElement>(null);

    const handleTabClick = (tabId: string) => {
        // Change tab state
        setActiveTab(tabId);

        // Perform smooth scroll to the tabs section
        if (containerRef.current) {
            const element = containerRef.current;
            const bodyRect = document.body.getBoundingClientRect().top;
            const elementRect = element.getBoundingClientRect().top;
            const elementPosition = elementRect - bodyRect;
            const offsetPosition = elementPosition - stickyOffset;

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
        }
    };

    return (
        <div className={styles.tabsContainer} ref={containerRef}>
            {/* Tab Headers */}
            <div className={styles.tabHeaderList} style={{ top: `${stickyOffset}px` }}>
                {tabs.map((tab) => (
                    <button
                        key={tab.id}
                        className={`${styles.tabButton} ${activeTab === tab.id ? styles.active : ''}`}
                        onClick={() => handleTabClick(tab.id)}
                    >
                        {tab.label}
                        {activeTab === tab.id && <div className={styles.arrowDown}></div>}
                    </button>
                ))}
            </div>

            {/* Tab Content */}
            <div className={styles.tabContentPanel}>
                {renderTabContent()}
            </div>
        </div>
    );
};

export default ReportTabs;
